import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import {
  IStudentListQueryParams,
  TStudentPrimary,
} from '../../../../apis/students/students.interfaces';
import { useStudentListHook } from '../hooks/useStudentListHook';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';
import { useStudentFiltersState } from './studentFilters.context';
import { useGradeAndFeeStructureHook } from '../../institute/hooks/useGradeAndFeeStructureHook';
import isAppWithMock from 'src/utils/isAppWithMock';
import { shouldEnableStudentListQuery } from '../utils/studentListApiFilters';

type TStudentListAppState = {
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
};

enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_STUDENT_LIST = 'UPDATE_STUDENT_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.UPDATE_STUDENT_LIST; payload: TStudentPrimary[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialStudentListState: TStudentListAppState = {
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
};

const getSelectedFilterValue = (value?: string): string | undefined => {
  const selectedValue = value?.trim();
  return selectedValue && selectedValue !== 'select' ? selectedValue : undefined;
};

type TState = {
  studentListAppState: TStudentListAppState;
  studentList: TStudentPrimary[];
};

const StudentListContext = createContext<TState | undefined>(undefined);
const StudentListDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const StudentListReducer = (
  appState: TStudentListAppState,
  action: Action
): TStudentListAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...appState,
        userParams: { ...appState.userParams, ...action.payload },
      };
    case ACTION_TYPE.UPDATE_STUDENT_LIST:
      return { ...appState, ...action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...appState, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...appState, isLoading: false, errorMessage: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StudentListProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const filters = useStudentFiltersState();
  const withMock = isAppWithMock();
  const { data: classFeesData } = useGradeAndFeeStructureHook({
    enabled: !withMock,
  });
  const [studentListAppState, dispatch] = useReducer(
    StudentListReducer,
    initialStudentListState
  );
  const selectedClassId = getSelectedFilterValue(filters.grade);
  const selectedSectionId = getSelectedFilterValue(filters.section);

  const queryParams = useMemo<IStudentListQueryParams>(() => {
    const params: IStudentListQueryParams = {
      ...studentListAppState.userParams,
    };
    if (selectedClassId) {
      params.classId = selectedClassId;
    }
    if (selectedSectionId) {
      params.sectionId = selectedSectionId;
    }
    return params;
  }, [studentListAppState.userParams, selectedClassId, selectedSectionId]);

  const listQueryEnabled = shouldEnableStudentListQuery({
    selectedClassId,
    withMock,
    classFeesData,
  });

  const {
    data: studentListResponse,
    isLoading: studentListResponseLoading,
    isError: studentListResponseLoadingError,
    isSuccess: studentListResponseLoadingDataSuccess,
    error: studentListResponseDataError,
  } = useStudentListHook(queryParams, { enabled: listQueryEnabled });

  const loadingPageData = useMemo(
    () => studentListResponseLoading,
    [studentListResponseLoading]
  );

  const loadingPageDataSuccess = useMemo(
    () => studentListResponseLoadingDataSuccess,
    [studentListResponseLoadingDataSuccess]
  );

  const loadingPageDataError = useMemo(
    () => studentListResponseLoadingError,
    [studentListResponseLoadingError]
  );

  useEffect(() => {
    onStudentListStateChange();
    // eslint-disable-next-line
  }, [
    studentListResponseDataError,
    loadingPageDataError,
    studentListResponseLoading,
    loadingPageData,
    loadingPageDataSuccess,
    studentListResponseLoadingError,
  ]);

  const onStudentListStateChange = () => {
    if (loadingPageData) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (loadingPageDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (loadingPageDataError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload: studentListResponseLoadingError
          ? (studentListResponseDataError?.response?.data.message ??
            ERROR_MESSAGE.GET_STUDENT_LIST_FAILURE)
          : '',
      });
    }
  };

  const currentState = useMemo<TState>(
    () => ({
      studentListAppState,
      studentList: studentListResponse?.data ?? [],
      userParams: {
        page: studentListAppState.userParams.page || 1,
        limit: studentListAppState.userParams.limit || 10,
      },
    }),
    [studentListAppState, studentListResponse]
  );

  return (
    <StudentListContext.Provider value={currentState}>
      {children}
    </StudentListContext.Provider>
  );
};
export const useStudentListState = () => {
  const state = useContext(StudentListContext);
  if (state === undefined) {
    throw new Error(
      'useStudentListState must be used within a StudentListProvider'
    );
  }
  return state;
};

export const useStudentListActions = () => {
  const dispatch = useContext(StudentListDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useStudentListActions dispatch must be within a StudentListProvider'
    );
  }

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  return { updateSearchParams };
};
